import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Stack, Tooltip } from "@mui/material";
import { copyToClipboard } from "@src/appV2/redesign/utils/copyToClipboard";
import { useCallback, useState } from "react";

import { Text } from "../../../components/Text";
import { NavigationMenuSectionTitleDesktop } from "./MenuSectionTitle";

interface NavCopyableFieldProps {
  label: string;
  value: string;
}

function NavCopyableField(props: NavCopyableFieldProps) {
  const { label, value } = props;
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await copyToClipboard(value);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }, [value]);

  return (
    <Tooltip title={isCopied ? "Copied!" : "Tap to copy"}>
      <Box
        component="button"
        type="button"
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          paddingX: 3,
          paddingY: 2,
          width: "100%",
          border: "none",
          borderRadius: theme.borderRadius?.small,
          backgroundColor: theme.palette.navbar?.itemSelectedBackground,
          cursor: "pointer",
          transition: theme.transitions.create(["background-color"], {
            duration: theme.transitions.duration.short,
          }),
          "&:hover": {
            backgroundColor: `color-mix(in srgb, ${theme.palette.navbar?.itemSelectedBackground} 100%, white 5%)`,
          },
        })}
        onClick={handleCopy}
      >
        <Box sx={{ minWidth: 0 }}>
          <Text
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.navbar?.sectionTitle,
              lineHeight: 1.2,
            })}
          >
            {label}
          </Text>
          <Text
            semibold
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.navbar?.itemContent,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.3,
            })}
          >
            {value}
          </Text>
        </Box>
        <ContentCopyIcon
          sx={(theme) => ({
            fontSize: "0.875rem",
            color: isCopied
              ? theme.palette.navbar?.itemContentSelected
              : theme.palette.navbar?.sectionTitle,
            flexShrink: 0,
          })}
        />
      </Box>
    </Tooltip>
  );
}

interface CurrentlyViewedSectionDesktopProps {
  admin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
  currentName: string;
  currentUserId: string;
}

/**
 * Renders the "Currently Viewed" section: a clickable worker/workplace name link
 * followed by copyable Professional Name / ID fields.
 */
export function CurrentlyViewedSectionDesktop(props: CurrentlyViewedSectionDesktopProps) {
  const { admin, isWorker, isWorkplace, currentName, currentUserId } = props;

  // Only show for admin users that are currently posing as a workplace or worker
  if (!admin || (!isWorker && !isWorkplace)) {
    return null;
  }

  return (
    <>
      <NavigationMenuSectionTitleDesktop>Currently Viewed</NavigationMenuSectionTitleDesktop>
      <Stack gap={2}>
        <NavCopyableField
          label={isWorkplace ? "Workplace Name" : "Professional Name"}
          value={currentName}
        />
        <NavCopyableField
          label={isWorkplace ? "Workplace ID" : "Professional ID"}
          value={currentUserId}
        />
      </Stack>
    </>
  );
}
